<script>
	/* eslint-disable svelte/no-at-html-tags */
	// NPM Modules
	import dayjs from 'dayjs';
	import relativeTime from 'dayjs/plugin/relativeTime';
	import collect from 'collect.js';
	import Link from 'svelte-navaid/Link.svelte';
	import { tick } from 'svelte';
	// Common
	import {
		isBoxedBet,
		isEachWayBet,
		isExoticBet,
		isMultiBet,
		isSportBet,
		isSRMBet,
		isSGMBet,
		isSportLeg,
		isSportSinglesBet,
		isBonusBet,
		getBetOddsForDisplay,
		isBoostedBet,
		getBetPotentialDividendsForDisplay,
		isSGMLeg,
		hasPromotionOffer,
		isVirtualLeg,
		isRacingSinglesBet,
		isLiveBet
	} from '@/Common/Bet.js';
	// Constants
	import { BetSource, BetType } from '@/Constants/Bet.js';
	import { UserRatingColours } from '@/Constants/User.js';
	// Components
	import FontIcon from '@/Components/FontIcon.svelte';
	import TimeToStart from '@/Components/TimeToStart.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	// Partials
	import ActionReject from '@/Partials/Bets/_ActionReject.svelte';
	import ActionToWin from '@/Partials/Bets/_ActionToWin.svelte';
	import ActionPartial from '@/Partials/Bets/_ActionPartial.svelte';
	import WarningMessage from '@/Partials/User/_WarningMessage.svelte';
	import SGMBetDisplay from '@/Partials/Bets/_SGMBetDisplay.svelte';
	import SRMBetDisplay from '@/Partials/Bets/_SRMBetDisplay.svelte';
	import SportBetDisplay from '@/Partials/Bets/_SportBetDisplay.svelte';
	import LegOddsDisplay from '@/Partials/Bets/_LegOddsDisplay.svelte';
	import RacingBetDisplay from '@/Partials/Bets/_RacingBetDisplay.svelte';
	import MultiLegStatusDisplay from '@/Partials/Bets/_MultiLegStatusDisplay.svelte';
	import ExoticsBetDisplay from '@/Partials/Bets/_ExoticsBetDisplay.svelte';
	// Stores
	import IncomingBetStore from '@/Stores/IncomingBetStore.js';
	import ACLStore from '@/Stores/ACLStore.js';
	// Services
	import { notify } from '@/Services/Notify.js';
	import {
		routeForMeetingRace,
		routeForSportEvent,
		multiOdds,
		snakeCaseToTitleCase,
		getDivisor
	} from '@/Services/Helpers.js';
	import { CurrencyFormat, NumberTransform } from '@/Services/Helpers';
	import { getIconForSelection } from '@/Services/Helpers.js';
	// Models
	import Rule from '@/Models/Rule.js';

	dayjs.extend(relativeTime);

	//Common Vars
	$: acceptingBet = false;

	// exports/props
	export let bet;

	// SINGLE BETS
	$: selection = bet && bet.legs.data.first().leg_selections.data.first();
	$: fixedPrice = bet && bet.legs.data.first().fixed_price;
	$: hasRules = bet && bet.rules && bet.rules.data.count() > 0;
	$: ruleAction = bet && bet.rules && getRuleAction(bet.rules.data.first());

	let multiLegs = collect([]);
	let rejectOpen = false;
	let toWinOpen = false;
	let partialOpen = false;
	let betRule;

	$: liability = bet && calcLiability();

	//RULE DETAILS
	$: ruleId = bet && bet.rules && bet.rules.data.first()?.id.substring(bet.rules.data.first().id.indexOf('-') + 1);
	$: ruleId && getRuleDetails(ruleId);

	function getRuleAction(rule) {
		if (typeof rule === 'undefined') {
			return false;
		}
		if ('rule_meta' in rule) {
			if ('applied_action' in rule.rule_meta) {
				return rule.rule_meta.applied_action;
			}
		}
		return false;
	}

	function getRuleDetails(ruleId) {
		const rule = new Rule();
		rule.modelHasMeta = true;
		if (ruleId) {
			rule
				.find(ruleId)
				.then((result) => {
					betRule = result.data;
				})
				.catch((e) => {
					console.error(e);
				});
		}
	}

	// MULTI BETS
	$: bet && isMultiBet(bet) && setupMultiBet();

	function setupMultiBet() {
		let legs = collect([]);
		bet.legs.data.each((leg) => {
			let selection = leg.leg_selections.data.first();
			let legData = {
				id: leg.id,
				selection_id: selection.id,
				number: selection.number,
				name: selection.name,
				sport: selection.sport,
				sub_sport: selection.sub_sport,
				event: (selection.competition && selection.competition.name) || '-',
				sport_event: (selection.event && selection.event.name) || '-',
				event_number: (selection.event && selection.event.number) || '-',
				route_event: selection.event,
				route_meeting: selection.competition || null,
				bet_type: leg.bet_type,
				is_each_way: leg.is_each_way,
				is_boxed: leg.is_boxed,
				is_virtual: leg.is_virtual,
				status: leg.status,
				product: leg.product,
				fixed_price: leg.fixed_price,
				leg_number: leg.leg_number,
				selection_status: selection.status
			};

			if (isSGMLeg(leg)) {
				legData['selection'] = selection;
			}

			if (selection.market) {
				legData['market'] = selection.market;
			}

			legs.push(legData);
		});
		multiLegs = legs;
	}

	function calcLiability() {
		let odds = bet.legs.data.pluck('fixed_price');
		// Tote bets don't have fixed price, use potential_dividends if we have it
		if (
			!odds.count() &&
			(bet.bet_type.id === BetType.Win || bet.bet_type.id === BetType.Place) &&
			ruleAction &&
			ruleAction.params.potential_dividends
		) {
			Object.values(ruleAction.params.potential_dividends).forEach((value) => {
				odds.push(value);
			});
		}
		odds = isMultiBet(bet) ? multiOdds(bet) : odds.reduce((total, price) => total + price);
		odds = isEachWayBet(bet) ? odds / 2 : odds;
		let betAmount = NumberTransform(bet.bet_amount, false);
		if (odds) {
			let win = (betAmount * (odds / getDivisor())).toFixed(2);
			return (win - betAmount).toFixed(2) * 100;
		}
		return null;
	}

	function acceptBet() {
		acceptingBet = true;
		IncomingBetStore.acceptBet(bet)
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Accept Bet Failed',
					message: e.error[0] || 'Problem accepting bet',
					timeout: 5
				});
			})
			.finally(() => {
				acceptingBet = false;
			});
	}

	async function openRejectBet() {
		rejectOpen = !rejectOpen;
		await tick();
		window.scrollTo(0, 0);
	}

	async function openToWinBet() {
		toWinOpen = !toWinOpen;
		await tick();
		window.scrollTo(0, 0);
	}

	async function openPartialBet() {
		partialOpen = !partialOpen;
		await tick();
		window.scrollTo(0, 0);
	}
</script>

<div
	class="flex flex-col justify-center col-start-1 px-1 py-2 border-b pl-2"
	class:border-dashed={hasRules && ruleAction}
>
	<div>{dayjs(bet.created_at).format('LTS')}</div>
	<div class="text-xs text-red-500">
		<TimeToStart start_time={bet.created_at} />
	</div>
</div>
<div class="flex px-1 py-2 border-b text-sm justify-start items-center" class:border-dashed={hasRules && ruleAction}>
	<div class="flex flex-col">
		<!-- EVENT -->
		{#if isSportBet(bet)}
			<Link
				href={routeForSportEvent(selection.event)}
				class="focus:outline-none hover:underline flex flex-row w-full text-xs"
			>
				<div class="flex flex-row">
					<span
						>{(selection.event && selection.event.name) || '-'} ({(selection.competition &&
							selection.competition.name) ||
							'-'})
					</span>
					<div title={selection.sport.name} class="ml-1">
						<FontIcon icon={getIconForSelection(selection)} className="mr-1" />
					</div>
				</div>
			</Link>
		{:else if isMultiBet(bet)}
			{#each multiLegs && multiLegs.all() as leg (leg.id)}
				{#if !isVirtualLeg(leg)}
					{#if isSportLeg(leg) || isSGMLeg(leg)}
						<Link
							href={routeForSportEvent(leg.route_event)}
							class="focus:outline-none hover:underline flex flex-row w-full text-xs"
						>
							<div class="flex flex-row">
								<span class="font-semibold w-6">L{leg.leg_number}:</span>
								<span>{leg.sport_event} ({leg.route_meeting.name})</span>
								<div title={leg.sport.name} class="ml-1">
									<FontIcon icon={getIconForSelection(leg)} />
								</div>
							</div>
						</Link>
					{:else}
						<Link
							href={routeForMeetingRace(leg.route_meeting, leg.route_event)}
							class="focus:outline-none hover:underline flex flex-row w-full text-xs"
						>
							<div class="flex flex-row w-full">
								<span class="font-semibold w-6">L{leg.leg_number}:</span>
								<span>{leg.event} - Race {leg.event_number}</span>
								<div title={leg.sport.name} class="ml-1">
									<FontIcon icon={getIconForSelection(leg)} className="mr-1" />
								</div>
							</div>
						</Link>
					{/if}
				{/if}
			{/each}
		{:else}
			<Link
				href={routeForMeetingRace(selection.competition, selection.event)}
				class="focus:outline-none hover:underline flex flex-row w-full text-xs"
			>
				<div class="flex flex-row">
					<span>
						{selection.competition.name} - R{selection.event.number}
					</span>
					<div title={selection.sport.name} class="ml-1">
						<FontIcon icon={getIconForSelection(selection)} className="mr-1" />
					</div>
				</div>
			</Link>
		{/if}
	</div>
</div>
<div class="flex px-1 py-2 border-b text-sm justify-start items-center" class:border-dashed={hasRules && ruleAction}>
	<div class="flex flex-col text-xs">
		<div class="flex flex-row">
			<!-- SELECTION(s)-->
			{#if isSportSinglesBet(bet)}
				<SportBetDisplay {bet} {selection} canResult={false} />
			{:else if isSGMBet(bet)}
				<SGMBetDisplay {selection} canResult={false} />
			{:else if isRacingSinglesBet(bet)}
				<RacingBetDisplay {bet} {selection} />
				<LegOddsDisplay {bet} selection={bet.legs.data.first()} showGuided={true} />
			{:else if isExoticBet(bet)}
				<ExoticsBetDisplay {bet} fancyDisplay={true} />
			{:else if isSRMBet(bet)}
				<SRMBetDisplay {bet} />
			{:else if isMultiBet(bet)}
				<div class="flex flex-col">
					{#each multiLegs && multiLegs.all() as leg (leg.id)}
						{#if !isVirtualLeg(leg)}
							<div class="flex flex-row items-center space-x-1 mb-0.5">
								<MultiLegStatusDisplay {leg} {multiLegs} showStatus={false} />
								<div class="flex flex-row">
									{#if isSportLeg(leg)}
										<SportBetDisplay {bet} selection={leg} inMulti={true} canResult={false} />
									{:else if isSGMLeg(leg)}
										<SGMBetDisplay selection={leg} inMulti={true} canResult={false} />
									{:else}
										<RacingBetDisplay {bet} selection={leg} inMulti={true} />
									{/if}
								</div>
								<LegOddsDisplay {bet} selection={leg} showGuided={true} inMulti={true} />
							</div>
						{/if}
					{/each}
				</div>
			{/if}
		</div>
	</div>
</div>
<div
	class="flex flex-row flex-grow justify-start items-center border-b px-1 py-2"
	class:border-dashed={hasRules && ruleAction}
>
	<div class="flex flex-col">
		<div class="text-xs font-semibold">
			{(isBoxedBet(bet) ? 'Boxed ' : '') + bet.bet_type.name}
		</div>
		<div class="flex my-px items-center">
			{#if isBonusBet(bet)}
				<FontIcon icon={'gift'} className="text-white bg-gray-500 p-1 rounded-sm text-tiny mr-1 h-4" />
			{/if}
			{CurrencyFormat(bet.bet_amount)}
			<div class="mx-0.5 pb-0.8 flex flex-row whitespace-nowrap justify-center items-center">@</div>
			{getBetOddsForDisplay(bet)}
			{#if ruleAction && ruleAction.params && ruleAction.params.potential_dividends}
				<span class="ml-0.5 text-xs font-semibold">
					{getBetPotentialDividendsForDisplay(bet, ruleAction)}
				</span>
			{/if}
			{#if isBoostedBet(bet)}
				<FontIcon icon="boost-up" className="text-primary ml-1" />
			{/if}
			{#if hasPromotionOffer(bet)}
				<FontIcon icon="bonus-back" className="ml-1 text-primary" />
			{/if}
		</div>
		<div class="text-sm text-orange-500">To Win {liability ? CurrencyFormat(liability) : 'TBA'}</div>
	</div>
</div>
<div
	class="flex flex-row flex-grow justify-center items-center border-b px-1 py-2"
	class:border-dashed={hasRules && ruleAction}
>
	<div>
		{#if rejectOpen}
			<ActionReject bind:open={rejectOpen} {bet} />
		{/if}
		{#if toWinOpen}
			<ActionToWin bind:open={toWinOpen} amount={liability} {bet} />
		{/if}
		{#if partialOpen}
			<ActionPartial bind:open={partialOpen} {bet} />
		{/if}
		<div class="flex flex-wrap justify-center align-middle hover:bg">
			{#if $ACLStore && ACLStore.granted('bets.update')}
				<LoadingButton
					on:click={acceptBet}
					loading={acceptingBet}
					disabled={acceptingBet}
					className="m-1 btn btn-success"
				>
					Accept
				</LoadingButton>
				<LoadingButton on:click={openRejectBet} loading={false} disabled={acceptingBet} className="m-1 btn btn-danger">
					Reject
				</LoadingButton>
				<LoadingButton
					on:click={openToWinBet}
					loading={false}
					disabled={!fixedPrice || acceptingBet}
					className="m-1 btn {fixedPrice ? 'btn-warning' : 'btn-primary-action'}"
				>
					To Win
				</LoadingButton>
				<LoadingButton
					on:click={openPartialBet}
					loading={false}
					disabled={acceptingBet}
					className="m-1 btn btn-warning"
				>
					Partial
				</LoadingButton>
			{/if}
		</div>
	</div>
</div>
<div
	class="flex flex-col justify-center items-center px-1 text-xs text-center border-b align-middle {UserRatingColours[
		bet.user.rating.id
	]?.bg ?? 'bg-gray-300'} {UserRatingColours[bet.user.rating.id]?.text ?? 'text-dark'}"
	class:border-dashed={hasRules && ruleAction}
>
	<Link href="/users/{bet.user.id}" class="focus:outline-none hover:underline">
		<div>{bet.user.first_name} {bet.user.last_name} - {bet.user.name}</div>
	</Link>
	<div class="flex items-center justify-center mt-1 text-xs">
		<div>{bet.user.rating.name} {bet.user.sub_rating ? ' (' + bet.user.sub_rating.name + ') ' : ''}</div>
		<div class="ml-1">({bet.user.tier.name ? '$' + bet.user.tier.name : 'TBA'})</div>
	</div>
	{#if isLiveBet(bet)}
		<div class="flex items-center justify-center mt-1 text-xl animate-bounce">
			<div class="bg-white mx-auto text-orange-500 rounded-sm">
				***
				<FontIcon icon={'live-event'} className="ml-0.5" />
				***
			</div>
		</div>
	{:else if !isLiveBet(bet) && bet.bet_source.id === BetSource.Phone}
		<div class="flex items-center justify-center mt-1 text-xl animate-bounce">
			<div class="bg-indigo-500 mx-auto rounded-sm">*** PHONE BET ***</div>
		</div>
	{/if}
</div>
{#if hasRules && ruleAction}
	<div class="col-span-9 col-start-1 border-b bg-gray-100 pl-2">
		<div class="flex my-1 space-x-3 text-xs">
			{#if Object.keys(ruleAction.params).length}
				{#each Object.entries(ruleAction.params) as param, i (i)}
					{#if param[0] !== 'potential_dividends' && param[0] !== 'potential_d_dividends'}
						{#if param[0] !== 'price_roll'}
							{#if ruleAction.highlighted_params && ruleAction.highlighted_params.includes(param[0])}
								<div class="text-red-400">
									<span class="font-semibold">
										{snakeCaseToTitleCase(param[0])}:
									</span>
									{isNaN(param[1]) ? param[1] : CurrencyFormat(param[1])}
								</div>
							{:else}
								<div>
									<span class="font-semibold">
										{snakeCaseToTitleCase(param[0])}:
									</span>
									{isNaN(param[1]) ? param[1] : CurrencyFormat(param[1])}
								</div>
							{/if}
						{:else}
							<div>
								<span class="font-semibold">
									{snakeCaseToTitleCase(param[0])}:
								</span>
								{param[1]}
							</div>
						{/if}
					{/if}
				{/each}
			{:else}
				<div>
					<span class="font-semibold">
						{snakeCaseToTitleCase(ruleAction.name)}
					</span>
				</div>
			{/if}
			{#if betRule}
				<div>
					<span class="font-semibold"> Rule: </span>
					{betRule.name}
				</div>
			{/if}
		</div>
	</div>
{/if}
{#if bet.user.message}
	<div class="col-span-9 col-start-1">
		<WarningMessage user={bet.user} />
	</div>
{/if}
